import React, { useState, useEffect } from 'react';
import Spinner from '../../components/common/Spinner';
import { useNavigate, useParams } from 'react-router-dom';
import { getGigById, updateGig } from '../../services/gigService';
import { getCurrentUser } from '../../services/authService';
import Combobox from '../../components/common/Combobox';
import PlacesAutocomplete from '../../components/common/PlacesAutocomplete';
import instrumentsData from '../../data/instrumentsData';
import './CreateGig.css'; // Reuse the same CSS as CreateGig

/**
 * EditGig component for venues to edit their existing gigs
 */
const EditGig = () => {
  const navigate = useNavigate();
  const { id: gigId } = useParams();
  const [, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    duration: '',
    location: '',
    location_data: null,
    pay: '',
    payment_type: 'flat',
    position: '',
    instruments: [],
    experience_level: 'any',
    is_recurring: false
  });
  
  const [showMusicianOptions, setShowMusicianOptions] = useState(false);

  // Fetch the current user and the gig data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get current user
        const { user, error: userError } = await getCurrentUser();
        
        if (userError) throw userError;
        
        if (!user) {
          navigate('/login');
          return;
        }
        
        setUser(user);
        
        // Get the gig data
        const { gig, error: gigError } = await getGigById(gigId);
        
        if (gigError) throw gigError;
        
        if (!gig) {
          throw new Error('Gig not found');
        }
        
        // Check if the current user is the creator of the gig
        if (gig.created_by !== user.id) {
          throw new Error('You do not have permission to edit this gig');
        }
        
        // Format date and time for form inputs
        let formattedDate = '';
        let formattedTime = '';
        
        if (gig.date) {
          const gigDate = new Date(gig.date);
          formattedDate = gigDate.toISOString().split('T')[0];
          
          const hours = gigDate.getHours().toString().padStart(2, '0');
          const minutes = gigDate.getMinutes().toString().padStart(2, '0');
          formattedTime = `${hours}:${minutes}`;
        }
        
        // Set form data from gig
        setFormData({
          title: gig.title || '',
          description: gig.description || '',
          date: formattedDate,
          time: formattedTime,
          duration: gig.duration || '',
          location: gig.location || '',
          location_data: gig.location_data || null,
          pay: gig.pay ? gig.pay.toString() : '',
          payment_type: gig.payment_type || 'flat',
          position: gig.position || '',
          instruments: gig.instruments || [],
          experience_level: gig.experience_level || 'any',
          is_recurring: gig.is_recurring || false
        });
        
        // Show musician options if position is set
        if (gig.position) {
          setShowMusicianOptions(true);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message || 'An error occurred while loading the gig');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [gigId, navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // If position field is changed, show/hide musician options
    if (name === 'position') {
      setShowMusicianOptions(!!value);
    }
  };
  
  const handleInstrumentsChange = (selectedInstruments) => {
    setFormData({
      ...formData,
      instruments: selectedInstruments
    });
  };
  
  const handleLocationSelect = (location, locationData) => {
    setFormData({
      ...formData,
      location,
      location_data: locationData
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError(null);
      
      // Validate form
      if (!formData.title) {
        throw new Error('Title is required');
      }
      
      if (!formData.date) {
        throw new Error('Date is required');
      }
      
      // Create a date object from date and time
      let gigDateTime = null;
      
      if (formData.date) {
        const dateStr = formData.date;
        const timeStr = formData.time || '00:00';
        
        gigDateTime = new Date(`${dateStr}T${timeStr}`);
        
        if (isNaN(gigDateTime.getTime())) {
          throw new Error('Invalid date or time format');
        }
      }
      
      // Prepare the gig data for update
      const gigData = {
        title: formData.title,
        description: formData.description,
        date: gigDateTime ? gigDateTime.toISOString() : null,
        duration: formData.duration,
        location: formData.location,
        location_data: formData.location_data,
        pay: formData.pay ? parseFloat(formData.pay) : null,
        payment_type: formData.payment_type,
        position: formData.position,
        instruments: formData.instruments,
        experience_level: formData.experience_level,
        is_recurring: formData.is_recurring,
        updated_at: new Date().toISOString()
      };
      
      console.log('Updating gig with data:', gigData);
      
      // Update the gig
      const { gig, error } = await updateGig(gigId, gigData);
      
      if (error) throw error;
      
      console.log('Gig updated successfully:', gig);
      
      setSuccess(true);
      
      // Redirect to the gig details page after a short delay
      setTimeout(() => {
        navigate(`/gigs/${gigId}`, { 
          state: { message: 'Gig updated successfully!' } 
        });
      }, 2000);
    } catch (error) {
      console.error('Error updating gig:', error);
      setError(error.message || 'An error occurred while updating the gig');
      setSuccess(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <Spinner message="Loading gig details..." fullPage={true} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error!</h4>
          <p>{error}</p>
          <hr />
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/dashboard')}
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-gig-container container mt-4 mb-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h2 className="h4 mb-0">Edit Gig</h2>
            </div>
            <div className="card-body">
              {success && (
                <div className="alert alert-success" role="alert">
                  Gig updated successfully! Redirecting...
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                {/* Basic Information */}
                <div className="mb-4">
                  <h3 className="h5 mb-3">Basic Information</h3>
                  
                  <div className="mb-3">
                    <label htmlFor="title" className="form-label">Gig Title *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Pianist for Wedding Reception"
                    />
                  </div>
                  
                  <div className="mb-3">
                    <label htmlFor="description" className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="Describe the gig, requirements, and any additional details"
                    ></textarea>
                  </div>
                </div>
                
                {/* Date and Time */}
                <div className="mb-4">
                  <h3 className="h5 mb-3">Date and Time</h3>
                  
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="date" className="form-label">Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        id="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label htmlFor="time" className="form-label">Time</label>
                      <input
                        type="time"
                        className="form-control"
                        id="time"
                        name="time"
                        value={formData.time}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label htmlFor="duration" className="form-label">Duration</label>
                    <input
                      type="text"
                      className="form-control"
                      id="duration"
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      placeholder="e.g., 2 hours, 3-4 hours, etc."
                    />
                  </div>
                  
                  <div className="form-check mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="is_recurring"
                      name="is_recurring"
                      checked={formData.is_recurring}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="is_recurring">
                      This is a recurring gig
                    </label>
                  </div>
                </div>
                
                {/* Location */}
                <div className="mb-4">
                  <h3 className="h5 mb-3">Location</h3>
                  
                  <div className="mb-3">
                    <label htmlFor="location" className="form-label">Venue Location</label>
                    <PlacesAutocomplete
                      value={formData.location}
                      onSelect={handleLocationSelect}
                      placeholder="Enter venue address or location"
                    />
                  </div>
                </div>
                
                {/* Payment */}
                <div className="mb-4">
                  <h3 className="h5 mb-3">Payment</h3>
                  
                  <div className="row align-items-end">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="pay" className="form-label">Pay Amount</label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          type="number"
                          className="form-control"
                          id="pay"
                          name="pay"
                          value={formData.pay}
                          onChange={handleInputChange}
                          placeholder="e.g., 150"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label htmlFor="payment_type" className="form-label">Payment Type</label>
                      <select
                        className="form-select"
                        id="payment_type"
                        name="payment_type"
                        value={formData.payment_type}
                        onChange={handleInputChange}
                      >
                        <option value="flat">Flat Rate</option>
                        <option value="hourly">Hourly Rate</option>
                        <option value="negotiable">Negotiable</option>
                      </select>
                    </div>
                  </div>
                </div>
                
                {/* Musician Requirements */}
                <div className="mb-4">
                  <h3 className="h5 mb-3">Musician Requirements</h3>
                  
                  <div className="mb-3">
                    <label htmlFor="position" className="form-label">Position Needed</label>
                    <input
                      type="text"
                      className="form-control"
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      placeholder="e.g., Guitarist, Vocalist, DJ, etc."
                    />
                  </div>
                  
                  {showMusicianOptions && (
                    <>
                      <div className="mb-3">
                        <label htmlFor="instruments" className="form-label">Required Instruments</label>
                        <Combobox
                          options={instrumentsData}
                          selectedValues={formData.instruments}
                          onChange={handleInstrumentsChange}
                          placeholder="Select instruments..."
                          isMulti={true}
                        />
                      </div>
                      
                      <div className="mb-3">
                        <label htmlFor="experience_level" className="form-label">Experience Level</label>
                        <select
                          className="form-select"
                          id="experience_level"
                          name="experience_level"
                          value={formData.experience_level}
                          onChange={handleInputChange}
                        >
                          <option value="any">Any Experience Level</option>
                          <option value="beginner">Beginner (0-2 years)</option>
                          <option value="intermediate">Intermediate (3-5 years)</option>
                          <option value="experienced">Experienced (5-10 years)</option>
                          <option value="professional">Professional (10+ years)</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Submit Buttons */}
                <div className="d-flex justify-content-between mt-4">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate(-1)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Updating...
                      </>
                    ) : 'Update Gig'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditGig;
